import { useState, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { companyService } from "@/api/services"
import type { CompanyResponseDto } from "@/api/types/company.types"
import Sidebar from "@/components/Sidebar"
import WalletInfo from "@/components/WalletInfo"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Loader2, ArrowLeft, CheckCircle2, XCircle, User, ExternalLink, Coins } from "lucide-react"

export default function CompanyDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [company, setCompany] = useState<CompanyResponseDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Get ownerUserId from navigation state (passed from list page) - fallback only
  // Primary source is the API response which includes ownerUserId
  const ownerUserIdFromState = (location.state as { ownerUserId?: string })?.ownerUserId

  // Action states
  const [updating, setUpdating] = useState(false)
  const [allocating, setAllocating] = useState(false)
  const [allocateDialogOpen, setAllocateDialogOpen] = useState(false)
  const [allocateFormData, setAllocateFormData] = useState({
    credits: "",
    moto_quota: "",
    gear_quota: "",
    duration_in_days: "",
  })

  useEffect(() => {
    if (slug) {
      loadCompany()
    }
  }, [slug])

  const loadCompany = async () => {
    if (!slug) return
    setLoading(true)
    setError("")
    try {
      const data = await companyService.getCompanyBySlug(slug)
      setCompany(data)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load company"
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleVerified = async () => {
    if (!slug || !company) return

    setUpdating(true)
    setError("")
    setSuccess("")

    try {
      const updated = await companyService.updateCompany(slug, {
        verified: !company.verified,
      })
      setCompany(updated)
      setSuccess(`Company ${updated.verified ? "verified" : "unverified"} successfully`)
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to update company"
      setError(errorMessage)
    } finally {
      setUpdating(false)
    }
  }

  const handleViewOwnerUser = () => {
    // Primary: use company data from API (which includes ownerUserId)
    // Fallback: use state if company hasn't loaded yet
    const ownerUserId = company?.ownerUserId || ownerUserIdFromState
    if (ownerUserId) {
      navigate(`/users/${ownerUserId}`)
    } else {
      setError("Owner user ID not available")
    }
  }

  const handleAllocateCredits = async () => {
    if (!slug) return

    // Validate that at least one field is provided
    if (
      !allocateFormData.credits &&
      !allocateFormData.moto_quota &&
      !allocateFormData.gear_quota
    ) {
      setError("Please provide at least credits, moto quota, or gear quota")
      return
    }

    setAllocating(true)
    setError("")
    setSuccess("")

    try {
      const data: any = {}
      if (allocateFormData.credits) {
        data.credits = parseInt(allocateFormData.credits)
      }
      if (allocateFormData.moto_quota) {
        data.moto_quota = parseInt(allocateFormData.moto_quota)
      }
      if (allocateFormData.gear_quota) {
        data.gear_quota = parseInt(allocateFormData.gear_quota)
      }
      if (allocateFormData.duration_in_days) {
        data.duration_in_days = parseInt(allocateFormData.duration_in_days)
      }

      await companyService.allocateCredits(slug, data)
      setSuccess("Credits and quotas allocated successfully")
      setAllocateDialogOpen(false)
      setAllocateFormData({
        credits: "",
        moto_quota: "",
        gear_quota: "",
        duration_in_days: "",
      })
      // Reload company data to show updated info
      loadCompany()
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to allocate credits"
      setError(errorMessage)
    } finally {
      setAllocating(false)
    }
  }

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    )
  }

  if (!company) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
          <div className="max-w-2xl">
            <Card className="p-6">
              <div className="text-center">
                <p className="text-destructive mb-4">{error || "Company not found"}</p>
                <Button onClick={() => navigate("/companies")} variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Companies
                </Button>
              </div>
            </Card>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex">
      <Sidebar />

      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="sm" onClick={() => navigate("/companies")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Companies
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Company Details</h1>
          </div>

          {/* Success message */}
          {success && (
            <div className="bg-success/10 border border-success/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Company Information */}
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Company Information</h2>
              <div className="flex items-center gap-2">
                <Dialog open={allocateDialogOpen} onOpenChange={setAllocateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Coins className="mr-2 h-4 w-4" />
                      Allocate Credits/Quotas
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Allocate Credits and Ad Quotas</DialogTitle>
                      <DialogDescription>
                        Allocate credits and/or ad quotas to this company. Leave fields empty if
                        you don't want to allocate that resource.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="credits">Credits</Label>
                        <Input
                          id="credits"
                          type="number"
                          min="0"
                          placeholder="Enter credits amount"
                          value={allocateFormData.credits}
                          onChange={(e) =>
                            setAllocateFormData({ ...allocateFormData, credits: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="moto_quota">Moto Quota</Label>
                        <Input
                          id="moto_quota"
                          type="number"
                          min="0"
                          placeholder="Enter moto ad quota"
                          value={allocateFormData.moto_quota}
                          onChange={(e) =>
                            setAllocateFormData({ ...allocateFormData, moto_quota: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gear_quota">Gear Quota</Label>
                        <Input
                          id="gear_quota"
                          type="number"
                          min="0"
                          placeholder="Enter gear ad quota"
                          value={allocateFormData.gear_quota}
                          onChange={(e) =>
                            setAllocateFormData({ ...allocateFormData, gear_quota: e.target.value })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="duration_in_days">Duration (Days)</Label>
                        <Input
                          id="duration_in_days"
                          type="number"
                          min="1"
                          placeholder="Enter duration in days (e.g., 365)"
                          value={allocateFormData.duration_in_days}
                          onChange={(e) =>
                            setAllocateFormData({
                              ...allocateFormData,
                              duration_in_days: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setAllocateDialogOpen(false)}
                        disabled={allocating}
                      >
                        Cancel
                      </Button>
                      <Button onClick={handleAllocateCredits} disabled={allocating}>
                        {allocating ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Allocating...
                          </>
                        ) : (
                          "Allocate"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button
                  variant={company.verified ? "outline" : "default"}
                  size="sm"
                  onClick={handleToggleVerified}
                  disabled={updating}
                >
                  {company.verified ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      Unverify
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Verify
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="text-foreground font-medium">{company.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Slug</Label>
                  <p className="text-foreground font-medium">
                    <code className="text-xs bg-muted px-2 py-1 rounded">{company.slug}</code>
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge variant={company.verified ? "default" : "secondary"}>
                      {company.verified ? "Verified" : "Not Verified"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Headquarters City</Label>
                  <p className="text-foreground font-medium">
                    {company.headquartersCity || "N/A"}
                  </p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Headquarters Address</Label>
                  <p className="text-foreground font-medium">
                    {company.headquartersAddress || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Website</Label>
                  <p className="text-foreground font-medium">
                    {company.website ? (
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {company.website}
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">VAT</Label>
                  <p className="text-foreground font-medium">{company.vat || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Tax Number</Label>
                  <p className="text-foreground font-medium">{company.taxNumber || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Public Contact Email</Label>
                  <p className="text-foreground font-medium">
                    {company.publicContactEmail || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Public Contact Phone</Label>
                  <p className="text-foreground font-medium">
                    {company.publicContactPhone || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Years in Business</Label>
                  <p className="text-foreground font-medium">
                    {company.yearsInBusiness || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Vehicle Origins</Label>
                  <p className="text-foreground font-medium">
                    {company.vehicleOrigins || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Buying with Loan</Label>
                  <p className="text-foreground font-medium">
                    {company.buyingWithLoan || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Extra Services</Label>
                  <p className="text-foreground font-medium">
                    {company.extraServices || "N/A"}
                  </p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Authorized Brands</Label>
                  {company.authorizedBrands && company.authorizedBrands.length > 0 ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {company.authorizedBrands.map((brand) => (
                        <Badge key={brand.id} variant="outline" className="text-sm">
                          {brand.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-foreground font-medium">N/A</p>
                  )}
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Description</Label>
                  <p className="text-foreground font-medium">{company.description || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Owner User</Label>
                  {company?.ownerUserId ? (
                    <div className="mt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleViewOwnerUser}
                        className="text-sm"
                      >
                        <User className="mr-2 h-4 w-4" />
                        View Owner User
                        <ExternalLink className="ml-2 h-3 w-3" />
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1 break-all">
                        ID: {company.ownerUserId}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Note: Owner can deactivate their account to shut down the company
                      </p>
                    </div>
                  ) : ownerUserIdFromState ? (
                    <div className="mt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleViewOwnerUser}
                        className="text-sm"
                        disabled={loading}
                      >
                        <User className="mr-2 h-4 w-4" />
                        View Owner User
                        <ExternalLink className="ml-2 h-3 w-3" />
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1 break-all">
                        ID: {ownerUserIdFromState}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Loading company details...
                      </p>
                    </div>
                  ) : (
                    <p className="text-foreground font-medium">N/A</p>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Created At</Label>
                  <p className="text-foreground font-medium">
                    {new Date(company.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Updated At</Label>
                  <p className="text-foreground font-medium">
                    {new Date(company.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Wallet Information */}
          {company.ownerUserId && <WalletInfo userId={company.ownerUserId} />}

          {/* Contact Information */}
          {company.contact ? (
            <Card className="p-6 mb-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Contact Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">First Name</Label>
                  <p className="text-foreground font-medium">
                    {company.contact.firstName || "N/A"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Last Name</Label>
                  <p className="text-foreground font-medium">{company.contact.lastName || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Position</Label>
                  <p className="text-foreground font-medium">{company.contact.position || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p className="text-foreground font-medium">{company.contact.email || "N/A"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Phone Number</Label>
                  <p className="text-foreground font-medium">
                    {company.contact.phoneNumber || "N/A"}
                  </p>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="p-6 mb-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Contact Information</h2>
              <p className="text-muted-foreground">
                Contact information is only available for verified companies.
              </p>
            </Card>
          )}

          {/* Files Section */}
          {(company.logoFile || company.heroBannerFile || company.galleryImages?.length > 0) && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Files</h2>
              <div className="space-y-4">
                {company.logoFile && (
                  <div>
                    <Label className="text-muted-foreground">Logo</Label>
                    <div className="mt-2">
                      <img
                        src={company.logoFile.url}
                        alt={company.logoFile.name}
                        className="h-32 w-32 object-contain border rounded"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {company.logoFile.name}
                      </p>
                    </div>
                  </div>
                )}
                {company.heroBannerFile && (
                  <div>
                    <Label className="text-muted-foreground">Hero Banner</Label>
                    <div className="mt-2">
                      <img
                        src={company.heroBannerFile.url}
                        alt={company.heroBannerFile.name}
                        className="h-48 w-full object-cover border rounded"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {company.heroBannerFile.name}
                      </p>
                    </div>
                  </div>
                )}
                {company.galleryImages && company.galleryImages.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground">
                      Gallery Images ({company.galleryImages.length})
                    </Label>
                    <div className="mt-2 grid grid-cols-4 gap-4">
                      {company.galleryImages.map((image) => (
                        <div key={image.id}>
                          <img
                            src={image.url}
                            alt={image.name}
                            className="h-32 w-full object-cover border rounded"
                          />
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {image.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      </main>

    </div>
  )
}

